import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import { LocaleSchema, type Locale } from '@saniplus/contracts';

import { randomUuidV4 } from '../uuid';

/**
 * The `meta` store (D-06, D-10): the device uuid and the UI locale.
 *
 * Version 2 also creates the four stores the worker PWA's offline feature
 * needs (`clockAnchor`, `punchQueue`, `failedPunches`, `contextCache`) in the
 * same database. They were originally sketched as living in their own
 * `saniplus-worker-offline` database purely because this file pinned
 * `META_DB_VERSION` at 1 — opening the *same* name at version 1 against an
 * already-version-2 database throws `VersionError`, which would have broken
 * every `getOrCreateDeviceUuid` / `getLocale` call. Creating the stores here
 * instead removes that trap. This file never reads or writes them — they are
 * the offline feature's record, not this package's — they are declared here
 * only so the one `upgrade` transaction that owns this database creates them.
 */
export const META_STORE = 'meta';
export const CLOCK_ANCHOR_STORE = 'clockAnchor';
export const PUNCH_QUEUE_STORE = 'punchQueue';
export const FAILED_PUNCHES_STORE = 'failedPunches';
export const CONTEXT_CACHE_STORE = 'contextCache';
export const META_DB_VERSION = 2;

export interface MetaRecord {
  key: string;
  value: string;
}

interface MetaDb extends DBSchema {
  [META_STORE]: { key: string; value: MetaRecord };
  [CLOCK_ANCHOR_STORE]: { key: string; value: unknown };
  [PUNCH_QUEUE_STORE]: { key: string; value: unknown };
  [FAILED_PUNCHES_STORE]: { key: string; value: unknown };
  [CONTEXT_CACHE_STORE]: { key: string; value: unknown };
}

const DEVICE_UUID_KEY = 'deviceUuid';
const LOCALE_KEY = 'locale';

/**
 * API-compatible with version 1: same signature, same behaviour for `meta`.
 * `objectStoreNames.contains` guards make every store creation idempotent, so
 * this also runs harmlessly the first time a brand-new database is opened
 * (`oldVersion` 0) and when upgrading an existing version-1 database.
 */
export function openMetaDb(dbName: string): Promise<IDBPDatabase<MetaDb>> {
  return openDB<MetaDb>(dbName, META_DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(CLOCK_ANCHOR_STORE)) {
        db.createObjectStore(CLOCK_ANCHOR_STORE, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(PUNCH_QUEUE_STORE)) {
        db.createObjectStore(PUNCH_QUEUE_STORE, { keyPath: 'clientPunchUuid' });
      }
      if (!db.objectStoreNames.contains(FAILED_PUNCHES_STORE)) {
        db.createObjectStore(FAILED_PUNCHES_STORE, { keyPath: 'clientPunchUuid' });
      }
      if (!db.objectStoreNames.contains(CONTEXT_CACHE_STORE)) {
        db.createObjectStore(CONTEXT_CACHE_STORE, { keyPath: 'key' });
      }
    },
  });
}

export async function getMeta(dbName: string, key: string): Promise<string | null> {
  const db = await openMetaDb(dbName);
  try {
    const record = await db.get(META_STORE, key);
    return record?.value ?? null;
  } finally {
    db.close();
  }
}

export async function setMeta(dbName: string, key: string, value: string): Promise<void> {
  const db = await openMetaDb(dbName);
  try {
    await db.put(META_STORE, { key, value });
  } finally {
    db.close();
  }
}

export async function deleteMeta(dbName: string, key: string): Promise<void> {
  const db = await openMetaDb(dbName);
  try {
    await db.delete(META_STORE, key);
  } finally {
    db.close();
  }
}

/**
 * D-06: "The PWA generates a random UUID v4 in IndexedDB on first run."
 *
 * Never localStorage (evictable and readable by any script on the origin) and
 * never cleared on logout — the device stays the same device when the worker
 * signs back in.
 */
export async function getOrCreateDeviceUuid(dbName: string): Promise<string> {
  const existing = await getMeta(dbName, DEVICE_UUID_KEY);
  if (existing !== null && existing.length > 0) return existing;
  const created = randomUuidV4();
  await setMeta(dbName, DEVICE_UUID_KEY, created);
  return created;
}

/** The stored UI locale, or `null` before the user has ever chosen one. */
export async function getLocale(dbName: string): Promise<Locale | null> {
  const stored = await getMeta(dbName, LOCALE_KEY);
  const parsed = LocaleSchema.safeParse(stored);
  return parsed.success ? parsed.data : null;
}

export async function setLocale(dbName: string, locale: Locale): Promise<void> {
  await setMeta(dbName, LOCALE_KEY, locale);
}
